import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LygoLogo } from '@/components/LygoLogo';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { formatDate } from '../../lib/admin-utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  Activity,
  BarChart3,
  Link2,
  Upload,
  Users,
  User,
  Settings,
  Building2,
  Bell,
  RefreshCw,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  Plus,
  Shield,
  CircleHelp,
  Menu,
} from 'lucide-react';

export default function AdminLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { email, logout, profile } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const notificationPanelRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    loading,
    notice,
    isPlatformAdmin,
    tenants,
    selectedTenantId,
    setSelectedTenantId,
    refreshTenants,
    notifications,
    unreadNotifications,
    notificationsLoading,
    refreshNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    setNotice,
  } = useAdmin();

  const NAV_ITEMS = [
    { to: '/overview', label: t('nav.overview'), icon: BarChart3 },
    { to: '/analytics', label: t('nav.analytics'), icon: Activity },
    { to: '/links', label: t('nav.links'), icon: Link2 },
    { to: '/imports', label: t('nav.imports'), icon: Upload },
    { to: '/members', label: t('nav.members'), icon: Users },
    { to: '/settings', label: t('nav.settings'), icon: Settings },
  ];

  function viewTitle(pathname: string): string {
    if (pathname.startsWith('/workspaces')) return t('layout.viewTitles.workspaces');
    if (pathname.startsWith('/profile')) return t('layout.viewTitles.profile');
    if (pathname.startsWith('/account')) return t('layout.viewTitles.workspaces');
    if (pathname.startsWith('/platform')) return t('layout.viewTitles.platform');
    if (pathname.startsWith('/analytics')) return t('layout.viewTitles.analytics');
    if (pathname.match(/^\/links\/.+/)) return t('layout.viewTitles.linkDetail');
    if (pathname.startsWith('/links')) return t('layout.viewTitles.links');
    if (pathname.startsWith('/members')) return t('layout.viewTitles.members');
    if (pathname.match(/^\/imports\/.+\/analytics/)) return t('layout.viewTitles.importAnalytics');
    if (pathname.match(/^\/imports\/.+/)) return t('layout.viewTitles.importDetail');
    if (pathname.startsWith('/imports')) return t('layout.viewTitles.imports');
    if (pathname.startsWith('/settings')) return t('layout.viewTitles.settings');
    if (pathname.startsWith('/help')) return t('layout.viewTitles.help');
    return t('layout.viewTitles.default');
  }

  // Close popovers on outside click / escape
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (notificationsOpen && !notificationPanelRef.current?.contains(target)) {
        setNotificationsOpen(false);
      }
      if (userMenuOpen && !userMenuRef.current?.contains(target)) {
        setUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [notificationsOpen, userMenuOpen]);

  // Close popovers on route change
  useEffect(() => {
    setNotificationsOpen(false);
    setUserMenuOpen(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative flex h-screen overflow-hidden">
      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/45 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      )}
      {/* ── Sidebar ── */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 flex w-[86vw] max-w-72 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-2xl transition-transform lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 lg:shadow-none',
        mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-5">
          <LygoLogo compact dark />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-sidebar-foreground/60">{t('layout.brand')}</p>
            <h1 className="text-sm font-bold font-heading">{t('layout.brandSub')}</h1>
          </div>
        </div>

        <Separator className="bg-sidebar-border" />

        {/* Workspace switcher — single canonical place */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] uppercase tracking-wider text-sidebar-foreground/50">{t('layout.workspace')}</p>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-sidebar-foreground/50 hover:text-sidebar-foreground"
                onClick={() => {
                  refreshTenants().catch((error: unknown) => {
                    const message = error instanceof Error ? error.message : t('layout.errorRefreshTenants');
                    setNotice({ type: 'error', message });
                  });
                }}
                title={t('layout.refreshWorkspaces')}
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {tenants.length === 0 ? (
              <NavLink
                to="/workspaces"
                className="flex items-center justify-center gap-2 rounded-md border border-dashed border-sidebar-border bg-sidebar-accent/20 px-3 py-3 text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground transition-colors"
              >
              <Plus className="h-4 w-4" />
              <span>{t('layout.createFirstWorkspace')}</span>
            </NavLink>
          ) : (
            <>
              <select
                className="w-full rounded-md border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-1.5 text-sm font-medium text-sidebar-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                value={selectedTenantId || ''}
                onChange={(event) => setSelectedTenantId(event.target.value || null)}
              >
                {tenants.map((tenant) => (
                  <option key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName}
                  </option>
                ))}
              </select>
              <NavLink
                to="/workspaces"
                className="mt-1.5 flex items-center gap-1.5 text-[11px] text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
              >
                <Settings className="h-3 w-3" />
                <span>{t('layout.manageWorkspaces')}</span>
              </NavLink>
            </>
          )}
        </div>

        <Separator className="bg-sidebar-border" />

        {/* Navigation */}
        <div className="flex-1 px-3 py-4">
          <nav className="flex flex-col gap-1" aria-label="Admin navigation">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Platform admin section */}
          {isPlatformAdmin && (
            <>
              <Separator className="bg-sidebar-border my-3" />
              <p className="mb-2 px-2 text-[11px] uppercase tracking-wider text-sidebar-foreground/50">{t('nav.platform')}</p>
              <NavLink
                to="/platform"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                  )
                }
              >
                <Shield className="h-4 w-4" />
                <span>{t('nav.platformAdmin')}</span>
              </NavLink>
            </>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="workspace-header flex items-center justify-between border-b bg-card px-3 py-3 sm:px-4 lg:px-6 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 lg:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
              >
                <Menu className="h-4 w-4" />
              </Button>
              <h2 className="text-base font-bold font-heading sm:text-lg">{viewTitle(location.pathname)}</h2>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <NavLink to="/help" className={({ isActive }) => cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors sm:gap-1.5 sm:px-2.5',
              isActive
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input text-muted-foreground hover:text-foreground hover:bg-muted/60',
            )}
            >
              <CircleHelp className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('nav.help')}</span>
            </NavLink>

            {/* Notifications */}
            <div className="relative" ref={notificationPanelRef}>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setNotificationsOpen((current) => !current)}
                aria-expanded={notificationsOpen}
                aria-haspopup="dialog"
                aria-label={`${t('layout.notifications')}${unreadNotifications > 0 ? `, ${t('layout.notificationsUnread_other', { count: unreadNotifications })}` : ''}`}
              >
                <Bell className="h-5 w-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                    {unreadNotifications}
                  </span>
                )}
              </Button>

              {notificationsOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-1rem)] max-w-sm rounded-xl border border-primary/15 bg-card shadow-[0_16px_40px_rgba(15,23,42,0.14)] sm:max-w-md">
                  <div className="flex items-center justify-between border-b border-primary/10 p-4">
                    <div>
                      <h3 className="font-semibold">{t('layout.notifications')}</h3>
                      <p className="text-xs text-muted-foreground">{t('layout.notificationsUnread_other', { count: unreadNotifications })}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          refreshNotifications().catch((error: unknown) => {
                            const message = error instanceof Error ? error.message : t('layout.errorRefreshNotifications');
                            setNotice({ type: 'error', message });
                          });
                        }}
                      >
                        {t('common.refresh')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          markAllNotificationsRead()
                            .then(() => setNotice({ type: 'success', message: t('layout.notificationsMarked') }))
                            .catch((error: unknown) => {
                              const message = error instanceof Error ? error.message : t('layout.errorMarkNotifications');
                              setNotice({ type: 'error', message });
                            });
                        }}
                        disabled={unreadNotifications === 0}
                      >
                        {t('layout.notificationsMarkAll')}
                      </Button>
                    </div>
                  </div>

                  <ScrollArea className="max-h-96">
                    {notificationsLoading ? (
                      <p className="p-4 text-sm text-muted-foreground">{t('common.loading')}</p>
                    ) : notifications.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">{t('layout.notificationsEmpty')}</p>
                    ) : (
                      <div className="divide-y">
                        {notifications.slice(0, 12).map((item) => (
                          <article
                            key={item.notificationId}
                            className={cn(
                              'p-4 transition-colors',
                              item.readAt ? 'opacity-65' : 'bg-cyan-50/60',
                            )}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-sm font-semibold">{item.title}</span>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(item.createdAt)}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{item.message}</p>
                            <div className="flex gap-2">
                              {item.actionUrl && (
                                <NavLink to={item.actionUrl} onClick={() => setNotificationsOpen(false)}>
                                  <Button variant="outline" size="sm">{t('layout.notificationOpen')}</Button>
                                </NavLink>
                              )}
                              {!item.readAt && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    markNotificationRead(item.notificationId).catch((error: unknown) => {
                                      const message = error instanceof Error ? error.message : t('layout.errorMarkNotification');
                                      setNotice({ type: 'error', message });
                                    });
                                  }}
                                >
                                  {t('layout.notificationMarkRead')}
                                </Button>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>

            {/* User menu — single canonical place for identity, account link, logout */}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                  'hover:bg-accent',
                  userMenuOpen && 'bg-accent',
                )}
                onClick={() => setUserMenuOpen((current) => !current)}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {profile?.firstName
                    ? profile.firstName[0].toUpperCase()
                    : email ? email[0].toUpperCase() : '?'}
                </div>
                 <span className="hidden md:inline max-w-[120px] truncate lg:max-w-[140px]">
                  {profile?.firstName || profile?.lastName
                    ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
                    : email}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-[min(16rem,calc(100vw-1rem))] rounded-lg border bg-card shadow-lg">
                  <div className="p-4 border-b">
                    {(profile?.firstName || profile?.lastName) && (
                      <p className="text-sm font-medium truncate">
                        {[profile.firstName, profile.lastName].filter(Boolean).join(' ')}
                      </p>
                    )}
                    <p className="text-sm truncate text-muted-foreground">{email}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isPlatformAdmin ? t('layout.roleAdmin') : t('layout.roleUser')}
                    </p>
                  </div>
                  <div className="p-2">
                    <NavLink
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      <User className="h-4 w-4" />
                      {t('layout.profileMenu')}
                    </NavLink>
                    <NavLink
                      to="/workspaces"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      <Building2 className="h-4 w-4" />
                      {t('layout.workspacesMenu')}
                    </NavLink>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent text-left"
                      onClick={() => { setUserMenuOpen(false); logout(); }}
                    >
                      <LogOut className="h-4 w-4" />
                      {t('layout.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Notices */}
        {notice && (
          <div className="px-3 pt-3 sm:px-4 lg:px-6 lg:pt-4">
            <Alert variant={notice.type === 'error' ? 'destructive' : 'success'} className={notice.type === 'error' ? 'alert-error' : 'alert-success'}>
              {notice.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              <AlertDescription>{notice.message}</AlertDescription>
            </Alert>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 px-3 pt-2 text-sm text-muted-foreground sm:px-4 lg:px-6 lg:pt-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('layout.syncingWorkspace')}
          </div>
        )}

        {/* Page content */}
        <main className="safe-bottom flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
